// Lead import (LEAD-06, LEAD-16): read a spreadsheet saved as CSV from Excel or Google Sheets, or a Facebook lead
// export (often UTF-16 with tabs), and map its columns to what import_leads() takes. Every column ends up somewhere: in a
// field it was matched to, or as one of the form's answers. The server normalises and checks every number again; this
// only shapes the rows.
import type { ImportRow } from "./types";

/** The file's text, whatever its encoding: UTF-16 (Facebook's lead export), UTF-8 (Sheets), or Windows-1252 (old Excel). */
export function decodeText(bytes: Uint8Array): string {
  if (bytes.length >= 2 && bytes[0] === 0xff && bytes[1] === 0xfe) {
    return new TextDecoder("utf-16le").decode(bytes.subarray(2));
  }
  if (bytes.length >= 2 && bytes[0] === 0xfe && bytes[1] === 0xff) {
    return new TextDecoder("utf-16be").decode(bytes.subarray(2));
  }
  // UTF-16 without its mark: in text that is mostly ASCII, every other byte is zero.
  const sample = bytes.subarray(0, Math.min(bytes.length, 400));
  let oddZeros = 0;
  for (let i = 1; i < sample.length; i += 2) if (sample[i] === 0) oddZeros++;
  if (sample.length >= 4 && oddZeros >= sample.length / 2 * 0.6) return new TextDecoder("utf-16le").decode(bytes);
  const utf8 = new TextDecoder("utf-8").decode(bytes);
  return utf8.includes("�") ? new TextDecoder("windows-1252").decode(bytes) : utf8;
}

function detectDelimiter(text: string): string {
  const firstLine = text.slice(0, text.search(/\r?\n|$/));
  let best = ",";
  let bestCount = 0;
  for (const d of [",", ";", "\t"]) {
    let count = 0;
    let quoted = false;
    for (const c of firstLine) {
      if (c === '"') quoted = !quoted;
      else if (c === d && !quoted) count++;
    }
    if (count > bestCount) {
      best = d;
      bestCount = count;
    }
  }
  return best;
}

/** RFC 4180 with the usual real-world slack: BOM, CRLF or LF, ; or tab delimiters, blank lines skipped. */
export function parseCsv(text: string): string[][] {
  return parseCsvLines(text).rows;
}

/**
 * The same rows, with each row's line as a spreadsheet numbers it: blank lines count, and a quoted field with line
 * breaks in it is still one line. The skipped rows are reported by these numbers.
 */
export function parseCsvLines(text: string): { rows: string[][]; lines: number[] } {
  const body = text.charCodeAt(0) === 0xfeff ? text.slice(1) : text;
  const delimiter = detectDelimiter(body);
  const rows: string[][] = [];
  const lines: number[] = [];
  let line = 0;
  let row: string[] = [];
  let field = "";
  let quoted = false;
  let fieldStarted = false;

  const endRow = () => {
    row.push(field);
    line++;
    if (row.some((f) => f.trim() !== "")) {
      rows.push(row);
      lines.push(line);
    }
    row = [];
    field = "";
    fieldStarted = false;
  };

  for (let i = 0; i < body.length; i++) {
    const c = body[i];
    if (quoted) {
      if (c === '"') {
        if (body[i + 1] === '"') {
          field += '"';
          i++;
        } else {
          quoted = false;
        }
      } else {
        field += c;
      }
    } else if (c === '"' && !fieldStarted) {
      quoted = true;
      fieldStarted = true;
    } else if (c === delimiter) {
      row.push(field);
      field = "";
      fieldStarted = false;
    } else if (c === "\n" || c === "\r") {
      if (c === "\r" && body[i + 1] === "\n") i++;
      endRow();
    } else {
      field += c;
      fieldStarted = true;
    }
  }
  if (field !== "" || row.length > 0) endRow();
  return { rows, lines };
}

/** What a column can be. The person's own details first, then where the enquiry came from. */
export const FIELDS = [
  { key: "phone", label: "Phone number", part: "person" },
  { key: "name", label: "Name", part: "person" },
  { key: "last_name", label: "Last name", part: "person" },
  { key: "email", label: "Email", part: "person" },
  { key: "city", label: "City", part: "person" },
  { key: "requirement", label: "Looking for", part: "person" },
  { key: "location", label: "Location", part: "person" },
  { key: "budget", label: "Budget", part: "person" },
  { key: "owner_phone", label: "Owner's mobile number", part: "person" },
  { key: "enquired_at", label: "When they enquired", part: "source" },
  { key: "source", label: "Source", part: "source" },
  { key: "campaign_name", label: "Campaign", part: "source" },
  { key: "campaign_id", label: "Campaign ID", part: "source" },
  { key: "adset_name", label: "Ad set", part: "source" },
  { key: "adset_id", label: "Ad set ID", part: "source" },
  { key: "ad_name", label: "Ad", part: "source" },
  { key: "ad_id", label: "Ad ID", part: "source" },
  { key: "form_name", label: "Form", part: "source" },
  { key: "form_id", label: "Form ID", part: "source" },
  { key: "platform", label: "Platform", part: "source" },
  { key: "is_organic", label: "Organic", part: "source" },
  { key: "external_id", label: "Lead ID", part: "source" },
] as const;

export type FieldKey = (typeof FIELDS)[number]["key"];

export interface Mapping {
  hasHeader: boolean;
  /** The column of each field, or -1 when the file has none. */
  columns: Record<FieldKey, number>;
  /** A Facebook lead export: it names its ads, ad sets, campaigns and forms. */
  facebook: boolean;
}

const looksLikePhone = (s: string) => /^(p:)?\+?[\d\s()-]{8,}$/i.test(s.trim());

/** "Full Name", "full_name" and "What's your budget?" all read as words joined by underscores. */
export function headerKey(h: string): string {
  return h.trim().toLowerCase().replace(/[^\p{L}\p{N}]+/gu, "_").replace(/^_+|_+$/g, "");
}

// Exact names first, then looser patterns; a column is used for one field only.
const PATTERNS: [FieldKey, RegExp[]][] = [
  ["owner_phone", [/^(owner|agent|telecaller|caller|assigned)_.*(phone|mobile|number)/, /owner.*(phone|mobile)/]],
  ["phone", [
    /^(phone|phone_number|mobile|mobile_number|mobile_no|contact_number|contact_no|number|whatsapp|whatsapp_number|cell|cell_phone)$/,
    /phone|mobile|contact_n|whatsapp/,
  ]],
  ["ad_id", [/^ad_id$/]],
  ["ad_name", [/^(ad_name|ad)$/]],
  ["adset_id", [/^(adset_id|ad_set_id)$/]],
  ["adset_name", [/^(adset_name|ad_set_name|adset|ad_set)$/]],
  ["campaign_id", [/^campaign_id$/]],
  ["campaign_name", [/^(campaign_name|campaign)$/]],
  ["form_id", [/^form_id$/]],
  ["form_name", [/^(form_name|form)$/]],
  ["platform", [/^platform$/]],
  ["is_organic", [/^is_organic$/]],
  ["external_id", [/^(lead_id|leadgen_id)$/]],
  ["enquired_at", [
    /^(created_time|created_at|created|lead_date|enquiry_date|enquired_at|enquiry_time|submitted_at|submission_date|timestamp|date|time)$/,
    /created|enquir.*(date|time)|lead.*(date|time)/,
  ]],
  ["source", [/^(source|lead_source|utm_source|channel)$/]],
  ["last_name", [/^(last_name|surname|lastname)$/]],
  ["name", [/^(name|full_name|fullname|customer_name|lead_name|client_name|first_name|firstname)$/, /(^|_)name$/]],
  ["email", [/^(email|e_mail|email_address|email_id|mail)$/, /email/]],
  ["city", [/^(city|town|your_city)$/, /(^|_)city(_|$)/]],
  ["budget", [/budget|price_range/]],
  ["location", [/^(location|area|locality|preferred_location|preferred_area|address)$/, /location|locality/]],
  ["requirement", [
    /^(requirement|looking_for|interested_in|course|product|service|enquiry_for|property_type)$/,
    /interested|looking_for|requirement|which_course|course_interest/,
  ]],
];

/** Guess which column is which from the header row (or, with no header, from the values). */
export function guessMapping(rows: string[][]): Mapping {
  const first = rows[0] ?? [];
  const hasHeader = first.length > 0 && !first.some(looksLikePhone);
  const header = hasHeader ? first.map(headerKey) : [];
  const facebook = hasHeader &&
    ["ad_id", "adset_id", "campaign_id", "form_id", "created_time", "is_organic"].filter((k) => header.includes(k))
        .length >= 2;
  const columns = Object.fromEntries(FIELDS.map((f) => [f.key, -1])) as Record<FieldKey, number>;
  const used = new Set<number>();
  for (const [field, patterns] of PATTERNS) {
    for (const p of patterns) {
      const i = header.findIndex((h, idx) => !used.has(idx) && p.test(h));
      if (i >= 0) {
        columns[field] = i;
        used.add(i);
        break;
      }
    }
  }
  // Facebook names its own lead ID plainly "id"; in any other sheet "id" may be a row number.
  if (facebook && columns.external_id < 0) {
    const i = header.findIndex((h, idx) => !used.has(idx) && h === "id");
    if (i >= 0) columns.external_id = i;
  }
  if (!hasHeader) columns.phone = first.findIndex(looksLikePhone);
  return { hasHeader, columns, facebook };
}

/** The columns kept as the form's answers: everything not matched to a field. */
export function answerColumns(rows: string[][], mapping: Mapping): number[] {
  const width = Math.max(0, ...rows.slice(0, 50).map((r) => r.length));
  const mapped = new Set(Object.values(mapping.columns).filter((i) => i >= 0));
  return Array.from({ length: width }, (_, i) => i).filter((i) => !mapped.has(i));
}

/** How a column writes its dates: day first as in India, month first as a US spreadsheet does, or both (D-100). */
export type DateOrder = "dmy" | "mdy" | "mixed";

/**
 * Decided once for the whole column, from the dates that can be read only one way: "30/9" is day first, "9/30" month
 * first. With neither, day first. A column with both is "mixed", and each date is then read on its own.
 */
export function dateOrder(values: string[]): DateOrder {
  let dayFirst = false;
  let monthFirst = false;
  for (const v of values) {
    const m = v.trim().match(/^(\d{1,2})[/.-](\d{1,2})[/.-]\d{2,4}\b/);
    if (!m) continue;
    if (Number(m[1]) > 12) dayFirst = true;
    if (Number(m[2]) > 12) monthFirst = true;
  }
  return dayFirst && monthFirst ? "mixed" : monthFirst ? "mdy" : "dmy";
}

/**
 * When the customer enquired, as import_leads() reads it: ISO 8601 as it came (with its offset, or without one in the
 * company's time zone), a date with slashes in the column's order (see dateOrder; on its own, day first unless the day
 * cannot be the month), and Excel's day numbers. Anything else goes as written, so the server can say it did not
 * understand it.
 */
export function parseWhen(value: string, order: DateOrder = "mixed"): string | undefined {
  const v = value.trim();
  if (!v) return undefined;
  if (/^\d{4}-\d{2}-\d{2}([T ]\d{1,2}:\d{2}(:\d{2}(\.\d+)?)?)?(Z|[+-]\d{2}(:?\d{2})?)?$/i.test(v)) {
    return v.replace(" ", "T").replace(/Z$/i, "+00:00");
  }
  const pad = (n: number) => String(n).padStart(2, "0");
  const time = (h: number, m: number, s: number, ampm?: string) => {
    let hour = h;
    if (ampm) {
      const pm = ampm.toLowerCase() === "pm";
      if (hour === 12) hour = pm ? 12 : 0;
      else if (pm) hour += 12;
    }
    return hour <= 23 && m <= 59 && s <= 59 ? `${pad(hour)}:${pad(m)}:${pad(s)}` : undefined;
  };
  const slash = v.match(
    /^(\d{1,4})[/.-](\d{1,2})[/.-](\d{1,4})(?:[ ,T]+(\d{1,2}):(\d{2})(?::(\d{2}))?\s*(am|pm)?)?$/i,
  );
  if (slash) {
    const [, a, b, c, h, mi, s, ampm] = slash;
    let year: number, month: number, day: number;
    if (a.length === 4) [year, month, day] = [Number(a), Number(b), Number(c)];
    else {
      year = c.length === 2 ? 2000 + Number(c) : Number(c);
      const x = Number(a);
      const y = Number(b);
      [day, month] = order === "mdy" ? [y, x] : order === "dmy" || x > 12 || y <= 12 ? [x, y] : [y, x];
    }
    const t = h ? time(Number(h), Number(mi), Number(s ?? 0), ampm) : "00:00:00";
    if (month >= 1 && month <= 12 && day >= 1 && day <= 31 && year >= 2000 && t) {
      return `${year}-${pad(month)}-${pad(day)}T${t}`;
    }
    return v;
  }
  // Excel stores a date as days since 30 December 1899, the time as the fraction of a day.
  if (/^\d{5}(\.\d+)?$/.test(v)) {
    const days = Number(v);
    if (days > 36526 && days < 73051) {
      const at = new Date(Date.UTC(1899, 11, 30) + Math.round(days * 86400) * 1000);
      return `${at.getUTCFullYear()}-${pad(at.getUTCMonth() + 1)}-${pad(at.getUTCDate())}T${pad(at.getUTCHours())}:${
        pad(at.getUTCMinutes())
      }:${pad(at.getUTCSeconds())}`;
    }
  }
  return v;
}

/**
 * Rows ready for import_leads(), skipping rows without a phone. Each keeps its place in `rows` and its line in the file
 * (from parseCsvLines; without them, its place counted from 1).
 */
export function toImportRows(
  rows: string[][],
  mapping: Mapping,
  lines?: number[],
): { index: number; line: number; row: ImportRow }[] {
  const out: { index: number; line: number; row: ImportRow }[] = [];
  const header = mapping.hasHeader ? rows[0] : [];
  const answers = answerColumns(rows, mapping);
  const cell = (r: string[], i: number) => (i >= 0 ? (r[i] ?? "").trim() : "");
  const order = dateOrder((mapping.hasHeader ? rows.slice(1) : rows).map((r) => cell(r, mapping.columns.enquired_at)));
  rows.forEach((r, index) => {
    if (mapping.hasHeader && index === 0) return;
    const get = (field: FieldKey) => cell(r, mapping.columns[field]);
    // Facebook writes numbers as p:+91…, so a spreadsheet keeps the plus.
    const phone = get("phone").replace(/^[a-z]{1,3}:\s*/i, "");
    if (!phone) return;
    const row: ImportRow = { phone };
    const name = [get("name"), get("last_name")].filter(Boolean).join(" ");
    if (name) row.name = name;
    for (
      const field of [
        "email",
        "city",
        "requirement",
        "location",
        "budget",
        "owner_phone",
        "source",
        "campaign_name",
        "campaign_id",
        "adset_name",
        "adset_id",
        "ad_name",
        "ad_id",
        "form_name",
        "form_id",
        "platform",
        "is_organic",
        "external_id",
      ] as const
    ) {
      const value = get(field);
      if (value) row[field] = value;
    }
    const when = parseWhen(get("enquired_at"), order);
    if (when) row.enquired_at = when;
    const qa = answers
      .map((i) => ({ q: (header[i] ?? "").trim() || `Column ${i + 1}`, a: cell(r, i) }))
      .filter((x) => x.a !== "");
    if (qa.length) row.answers = qa;
    out.push({ index, line: lines?.[index] ?? index + 1, row });
  });
  return out;
}

/** Splits rows into batches the server finishes well inside a request's time (it takes at most 5,000 per call). */
export function batches<T>(items: T[], size = 250): T[][] {
  const out: T[][] = [];
  for (let i = 0; i < items.length; i += size) {
    out.push(items.slice(i, i + size));
  }
  return out;
}

/** A CSV a spreadsheet opens as it was: every field quoted when it needs to be, CRLF between rows. */
export function toCsv(rows: string[][]): string {
  // A semicolon is quoted too: spreadsheets in some countries split on it.
  const field = (f: string) => (/[",;\r\n]/.test(f) ? `"${f.replace(/"/g, '""')}"` : f);
  return rows.map((r) => r.map(field).join(",")).join("\r\n") + "\r\n";
}

/** "1 row", "3 rows". */
export function count(n: number, word: string): string {
  return `${n} ${word}${n === 1 ? "" : "s"}`;
}

/** What an import did, from its own counts: an enquiry is said to be recorded only when one was. */
export function importSummary(
  o: { inserted: number; updated: number; enquiries: number; repeats: number; problems: unknown[] },
): string {
  const parts = [count(o.inserted, "new customer")];
  if (o.updated) parts.push(`${o.updated} already in your CRM`);
  parts.push(
    o.enquiries ? `${o.enquiries} ${o.enquiries === 1 ? "enquiry" : "enquiries"} recorded` : "no new enquiries",
  );
  if (o.repeats) parts.push(`${count(o.repeats, "row")} already imported before`);
  if (o.problems.length) parts.push(`${count(o.problems.length, "row")} skipped`);
  return parts.join(", ") + ".";
}
